import { Link } from '@tanstack/react-router';
import { AlertCircle, MailCheck } from 'lucide-react';
import type React from 'react';
import { useState } from 'react';
import { AuthShell } from '@/components/auth/auth-shell';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { authClient } from '@/lib/auth-client';

/**
 * /forgot-password — request a reset link (plan 2026-07-24-password-reset).
 *
 * The server is enumeration-safe (same 200 + simulated timing for unknown
 * emails, password.mjs:51-64), so the UI mirrors it: ANY accepted request
 * lands on the same "check your inbox" state — never reveal whether the
 * email has an account. Also the social-only path: a Google-registered user
 * uses this to set their FIRST password (reset creates the credential
 * account, password.mjs:154-159).
 */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      // redirectTo is the SPA route better-auth's GET validates the token
      // against before 302ing here with ?token=… (originChecked server-side).
      const { error: requestError } = await authClient.requestPasswordReset({
        email,
        redirectTo: '/reset-password',
      });
      if (requestError) {
        setError('Something went wrong. Please try again.');
      } else {
        setSent(true);
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col">
      <AuthShell>
        {sent ? (
          <div>
            <div className="mb-6">
              <h1 className="text-lg font-semibold">Check your inbox</h1>
              <p className="mt-1 text-sm text-faint">
                If an account exists for {email}, we've sent a link to set a new password. It
                expires in 1 hour.
              </p>
            </div>
            <div className="flex items-center justify-center py-4 text-faint">
              <MailCheck className="h-10 w-10" />
            </div>
            <p className="mt-6 text-center text-sm text-faint">
              Remembered it?{' '}
              <Link to="/login" className="font-semibold text-foreground hover:underline">
                Back to sign in
              </Link>
            </p>
          </div>
        ) : (
          <div>
            <div className="mb-6">
              <h1 className="text-lg font-semibold">Reset your password</h1>
              <p className="mt-1 text-sm text-faint">
                Enter your account email and we'll send you a reset link.
              </p>
            </div>
            <form
              onSubmit={(e) => {
                void handleSubmit(e);
              }}
              className="space-y-4"
            >
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (error) setError(null);
                  }}
                  placeholder="you@example.com"
                />
              </div>
              <Button type="submit" size="cta" className="w-full" disabled={loading}>
                {loading ? 'Sending link…' : 'Send reset link'}
              </Button>
            </form>
            <p className="mt-6 text-center text-sm text-faint">
              Remembered it?{' '}
              <Link to="/login" className="font-semibold text-foreground hover:underline">
                Back to sign in
              </Link>
            </p>
          </div>
        )}
      </AuthShell>
    </div>
  );
}
